/**
 * `hierarchyPaths.ts` — hierarchy-path addressing shared by the context
 * engine and the tool executors.
 *
 * Post upstream UID-removal, Campfire AI tools address parts by hierarchy
 * paths like `"0,1,2"` (root entity index, then child indices — see the
 * "Hierarchy Path Format" section of the system prompt). Descendant segments
 * come from persisted `PartData.HierachyID`, the data-side counterpart of the
 * transform sibling indices Unity's `ResolvePartsByPath` walks.
 */

import {
  isOriginSnapshot,
  type EntityData,
  type EntitySnapshot,
} from "@models/data/entityData";

export interface PathEntry {
  /** Canonical path string, e.g. `"0"` (root model) or `"0,1,2"` (part). */
  readonly path: string;
  readonly snapshot: EntitySnapshot;
  /** Owning entity (root record). */
  readonly entity: EntityData;
  /** True when this snapshot is a top-level model root (single-index path). */
  readonly isRoot: boolean;
}

/**
 * Flatten a scene's entity trees into Unity-compatible path entries.
 *
 * Unity addresses descendants by live Transform sibling index. The persisted
 * `PartData.HierachyID` is the data-side copy of that address; using
 * `snapshot.children[]` positions instead silently compacts missing/wrapper
 * nodes and can resolve a valid path to the wrong part.
 */
export function buildPathIndex(entities: ReadonlyArray<EntityData>): PathEntry[] {
  const entries: PathEntry[] = [];
  let rootIndex = 0;
  for (const entity of entities) {
    const roots = entity.snapshots.filter((s) => s.isRoot);
    const list = roots.length > 0 ? roots : entity.snapshots.slice(0, 1);
    const shiftFirstChildIndex = !containsOriginSnapshot(entity.snapshots);
    for (const root of list) {
      walk(root, `${rootIndex}`, entity, true, shiftFirstChildIndex, entries);
      rootIndex++;
    }
  }
  return entries;
}

function walk(
  snapshot: EntitySnapshot,
  path: string,
  entity: EntityData,
  isRoot: boolean,
  shiftFirstChildIndex: boolean,
  out: PathEntry[],
): void {
  const resolvedPath = isRoot
    ? path
    : (pathFromHierarchyId(
        path.split(",", 1)[0],
        snapshot.hierachyID,
        shiftFirstChildIndex,
      ) ?? path);
  out.push({ path: resolvedPath, snapshot, entity, isRoot });
  snapshot.children.forEach((child, i) =>
    walk(child, `${resolvedPath},${i}`, entity, false, shiftFirstChildIndex, out));
}

function containsOriginSnapshot(snapshots: ReadonlyArray<EntitySnapshot>): boolean {
  for (const snapshot of snapshots) {
    if (isOriginSnapshot(snapshot) || containsOriginSnapshot(snapshot.children)) return true;
  }
  return false;
}

/**
 * Convert persisted `PartData.HierachyID` (`0,1,20,`) to an AI/tool path
 * (`<scene-root>,1,20`). The first hierarchy-id segment is the virtual entity
 * root marker, so the scene root index replaces it.
 *
 * Assets without a stored Origin snapshot get one inserted at runtime. Match
 * Unity/Web rendering by shifting the first real child index past that node.
 */
function pathFromHierarchyId(
  sceneRootIndex: string,
  hierarchyId: string | null,
  shiftFirstChildIndex: boolean,
): string | null {
  if (!hierarchyId) return null;
  const segments = hierarchyId
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length < 2 || segments.some((segment) => !/^\d+$/.test(segment))) return null;

  const childIndices = segments.slice(1).map(Number);
  if (shiftFirstChildIndex) childIndices[0]++;
  return [sceneRootIndex, ...childIndices].join(",");
}

export function resolveByPath(
  entities: ReadonlyArray<EntityData>,
  path: string,
): PathEntry | null {
  const normalized = normalizePath(path);
  if (!normalized) return null;
  return buildPathIndex(entities).find((e) => e.path === normalized) ?? null;
}

/** Resolve multiple paths, dropping ones that don't exist. */
export function resolveManyByPath(
  entities: ReadonlyArray<EntityData>,
  paths: ReadonlyArray<string>,
): PathEntry[] {
  const index = new Map(buildPathIndex(entities).map((e) => [e.path, e]));
  const resolved: PathEntry[] = [];
  for (const raw of paths) {
    const normalized = normalizePath(raw);
    if (!normalized) continue;
    const entry = index.get(normalized);
    if (entry) resolved.push(entry);
  }
  return resolved;
}

/** Root path (first index) for any path — the owning top-level model. */
export function rootPathOf(path: string): string {
  const normalized = normalizePath(path);
  const comma = normalized.indexOf(",");
  return comma === -1 ? normalized : normalized.slice(0, comma);
}

export function pathForSnapshotId(
  entities: ReadonlyArray<EntityData>,
  snapshotId: string,
): string | null {
  return buildPathIndex(entities).find((e) => e.snapshot.id === snapshotId)?.path ?? null;
}

/** Trim whitespace + trailing commas (Unity `HierachyID` strings carry them). */
export function normalizePath(path: string): string {
  return (path ?? "")
    .trim()
    .replace(/,\s*$/, "")
    .replace(/\s+/g, "");
}

const MOVED_EPSILON = 1e-4;

/** True when the snapshot's transform differs from its import placement. */
export function isMoved(snapshot: EntitySnapshot): boolean {
  const a = snapshot.transform;
  const b = snapshot.resetTransform;
  for (let i = 0; i < 3; i++) {
    if (Math.abs(a.position[i] - b.position[i]) > MOVED_EPSILON) return true;
    if (Math.abs(a.rotation[i] - b.rotation[i]) > MOVED_EPSILON) return true;
    if (Math.abs(a.scale[i] - b.scale[i]) > MOVED_EPSILON) return true;
  }
  return false;
}

export function displayNameOf(snapshot: EntitySnapshot): string {
  return snapshot.displayName || snapshot.name;
}

/**
 * One node in a scene, addressed both as an index path (`0,1,2`) and as a
 * slash-joined display-name path (`Robodog/Fan`). Name paths are what the
 * workflow authored view stores; index paths are what the AI tools use.
 *
 * Duplicate display names are left as-is. Callers that resolve a name path
 * take the FIRST match in this order — the same rule the attachment picker
 * implies by listing duplicates without a suffix.
 */
export interface DisplayNamePathEntry {
  readonly namePath: string;
  readonly indexPath: string;
  readonly snapshot: EntitySnapshot;
  readonly entity: EntityData;
  readonly isRoot: boolean;
}

/** Flatten a scene's entity trees into display-name + index path entries. */
export function buildDisplayNamePathIndex(
  entities: ReadonlyArray<EntityData>,
): DisplayNamePathEntry[] {
  const names = new Map<string, string>();
  const out: DisplayNamePathEntry[] = [];
  for (const entry of buildPathIndex(entities)) {
    const parentPath = entry.path.includes(",")
      ? names.get(entry.path.slice(0, entry.path.lastIndexOf(",")))
      : undefined;
    const name = displayNameOf(entry.snapshot).trim() || entry.path;
    const namePath = parentPath ? `${parentPath}/${name}` : name;
    names.set(entry.path, namePath);
    out.push({
      namePath,
      indexPath: entry.path,
      snapshot: entry.snapshot,
      entity: entry.entity,
      isRoot: entry.isRoot,
    });
  }
  return out;
}

/**
 * Part name aliases as a digest segment: ` (aka de:"Zahnrad", fr:"Engrenage")`,
 * or `""` when the part has none.
 *
 * Shared by the scene digest (`formatPartLine`) and `Get_Part_Context` so the
 * agent sees one spelling of the same fact. The grammar is documented in the
 * "Hierarchy Path Format" section of `public/AI/prompt/base.md` — keep the two
 * in step, per the registry/prompt discipline in `Campfire_AI.md`.
 *
 * This is what lets a user ask for a part by a name that is not its display
 * name: `querySceneContext` filters the digest by SUBSTRING, so an alias
 * printed on the line is searchable by construction, with no separate index to
 * build or keep in sync.
 */
export function aliasSegmentOf(snapshot: EntitySnapshot): string {
  if (snapshot.aliases.length === 0) return "";
  const parts = snapshot.aliases.map((a) => `${a.language}:"${a.value}"`);
  return ` (aka ${parts.join(", ")})`;
}
